import { ErrorHandler, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { LoginComponent } from './public/login/login.component';
import { ForgetComponent } from './public/forget/forget.component';
import { ResetComponent } from './public/reset/reset.component';
import { MainComponent } from './private/layout/main/main.component';
import { SharedModule } from './private/shared/shared.module';
import { MatButtonModule } from "@angular/material/button";
import { MatSidenavModule } from "@angular/material/sidenav";
import { MatDividerModule } from "@angular/material/divider";
import { MatToolbarModule } from "@angular/material/toolbar";
import { MatIconModule } from "@angular/material/icon";
import { MatMenuModule } from "@angular/material/menu";
import { MatListModule } from "@angular/material/list";
import { MatCardModule } from "@angular/material/card";
import { MatTableModule } from "@angular/material/table";
import { MatPaginatorModule } from "@angular/material/paginator";
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import {MatInputModule} from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { HttpResponseInterceptor } from './utils/HttpResponseInterceptor';
import { EncryptionInterceptor } from './utils/EncryptionInterceptor';
import { ToastrModule } from 'ngx-toastr';
import { GlobalErrorHandler } from './utils/GlobalErrorHandler';
import { ButtonComponent } from './components/button/button.component';
import {NgbModule} from "@ng-bootstrap/ng-bootstrap";
import { MatDatepickerModule, MatRangeDateSelectionModel } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatSortModule } from '@angular/material/sort';
import {MatChipInputEvent, MatChipsModule} from '@angular/material/chips';
import {MatAutocompleteSelectedEvent, MatAutocompleteModule} from '@angular/material/autocomplete';
import { AsyncPipe } from '@angular/common';
import { ManagementReportsComponent } from './private/modules/components/management-reports/management-reports.component';
import { ContactGroupsComponent } from './private/modules/configuration/contact-groups/contact-groups.component';
import { ContactGroupListComponent } from './private/modules/components/contact-group-list/contact-group-list.component';
import { CreateContactGroupComponent } from './private/modules/components/create-contact-group/create-contact-group.component';
import { AddContactsGroupComponent } from './private/modules/components/add-contacts-group/add-contacts-group.component';
import { CsvDropzoneComponent } from './private/modules/components/csv-dropzone/csv-dropzone.component';
import { NgxDropzoneModule } from 'ngx-dropzone';
import { WhatsappTemplateComponent } from './private/modules/configuration/whatsapp-template/whatsapp-template.component';
import { EmailTemplateComponent } from './private/modules/configuration/email-template/email-template.component';
import { SalesMailHubComponent } from './private/modules/configuration/sales-mail-hub/sales-mail-hub.component';
import { MailLeftActionsComponent } from './private/modules/components/sales-mail-hub/mail-left-actions/mail-left-actions.component';
import { MailMiddleSectionComponent } from './private/modules/components/sales-mail-hub/mail-middle-section/mail-middle-section.component';
import { MailRightSectionComponent } from './private/modules/components/sales-mail-hub/mail-right-section/mail-right-section.component';
import { MailRightTableComponent } from './private/modules/components/sales-mail-hub/mail-right-table/mail-right-table.component';
import { BroadcastCampaignComponent } from './private/modules/configuration/broadcast-campaign/broadcast-campaign.component';
import { CreateBroadcastCampaignComponent } from './private/modules/components/create-broadcast-campaign/create-broadcast-campaign.component';
import { EmailCampaignAnalyticsComponent } from './private/modules/components/email-campaign-analytics/email-campaign-analytics.component';
import { WhatsappCampaignAnalyticsComponent } from './private/modules/components/whatsapp-campaign-analytics/whatsapp-campaign-analytics.component';
import { ClientSettingComponent } from './private/modules/configuration/client-setting/client-setting.component';
import { PrescriptionConfigComponent } from './private/modules/configuration/client-setting/prescription-config/prescription-config.component';
import { DomainAuthenticationComponent } from './private/modules/configuration/domain-authentication/domain-authentication.component';
import { AddAuthDomainComponent } from './private/modules/components/auth-domain/add-auth-domain/add-auth-domain.component';
import { AddEmailToDomainComponent } from './private/modules/components/auth-domain/add-email-to-domain/add-email-to-domain.component';
import { EmailConfigComponent } from './private/modules/components/email-config/email-config.component';
import { CommunicationConfigComponent } from './private/modules/components/communication-config/communication-config.component';
import { WhatsappConfigComponent } from './private/modules/components/whatsapp-config/whatsapp-config.component';
import { CreateEmailTemplateComponent } from './private/modules/components/create-email-template/create-email-template.component';
import { PreviewEmailTemplateComponent } from './private/modules/components/preview-email-template/preview-email-template.component';
import { CreataeWhatsappTemplateComponent } from './private/modules/components/creatae-whatsapp-template/creatae-whatsapp-template.component';
import { SafeUrlPipe } from './safe-url.pipe';
import { SafeHtmlPipe } from './safe-html.pipe';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatBottomSheetModule } from '@angular/material/bottom-sheet';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatTabsModule } from '@angular/material/tabs';
import { MatCheckboxModule } from '@angular/material/checkbox';
// CKEditorModule compatibility - comment out if not explicitly needed, use standalone component approach instead
// import { CKEditorModule } from '@ckeditor/ckeditor5-angular';
import { AiScoringGuidelinesComponent } from './private/modules/configuration/ai-scoring-guidelines/ai-scoring-guidelines.component';
import { ChatbotComponent } from './private/shared/component/chatbot/chatbot.component';

const materialModules = [
  FormsModule,
  ReactiveFormsModule,
  MatButtonModule,
  NgbModule,
  ToastrModule.forRoot({ timeOut: 1500,
    positionClass: 'toast-bottom-right',
    preventDuplicates: true,}),
  MatFormFieldModule,
  MatChipsModule,
  MatAutocompleteModule,
  MatInputModule,
  MatSelectModule,
  MatSidenavModule,
  MatDividerModule,
  MatToolbarModule,
  MatIconModule,
  MatMenuModule,
  MatListModule,
  MatCardModule,
  MatTableModule,
  MatPaginatorModule,
  MatDatepickerModule,
  MatNativeDateModule ,
  MatSortModule,
  MatProgressBarModule,
  MatTabsModule,
  MatCheckboxModule,
  MatBottomSheetModule,
  AsyncPipe,
];

@NgModule({
  declarations: [
    AppComponent,
    LoginComponent,
    ForgetComponent,
    ResetComponent,
    MainComponent,
    ButtonComponent,
    ManagementReportsComponent,
    ContactGroupsComponent,
    ContactGroupListComponent,
    CreateContactGroupComponent,
    AddContactsGroupComponent,
    CsvDropzoneComponent,
    WhatsappTemplateComponent,
    EmailTemplateComponent,
    SalesMailHubComponent,
    MailLeftActionsComponent,
    MailMiddleSectionComponent,
    MailRightSectionComponent,
    MailRightTableComponent,
    BroadcastCampaignComponent,
    CreateBroadcastCampaignComponent,
    EmailCampaignAnalyticsComponent,
    WhatsappCampaignAnalyticsComponent,
    ClientSettingComponent,
    PrescriptionConfigComponent,
    EmailConfigComponent,
    CommunicationConfigComponent,
    WhatsappConfigComponent,
    CreateEmailTemplateComponent,
    PreviewEmailTemplateComponent,
    CreataeWhatsappTemplateComponent,
    SafeUrlPipe,
    SafeHtmlPipe,
    DomainAuthenticationComponent,
    AddAuthDomainComponent,
    AddEmailToDomainComponent,
    AiScoringGuidelinesComponent,
    ChatbotComponent,
  ],
  imports: [
    HttpClientModule,
    BrowserModule,
    AppRoutingModule,
    SharedModule,
    BrowserAnimationsModule,
    materialModules,
    NgxDropzoneModule,
 
 
  

   
    
  
  ],
  providers: [MatPaginatorModule,NgbModule,
    [{provide: ErrorHandler,useClass: GlobalErrorHandler},
      { provide: HTTP_INTERCEPTORS, useClass: EncryptionInterceptor, multi: true },
      { provide: HTTP_INTERCEPTORS, useClass: HttpResponseInterceptor, multi:true}
    ],
    // if deployed on subdomain
    // {provide: LocationStrategy, useClass: HashLocationStrategy},

    MatRangeDateSelectionModel,
    MatNativeDateModule,
    MatDatepickerModule,
  ],
  bootstrap: [AppComponent]
})
export class AppModule { }

